import { useState, useRef, useEffect } from "react";
import { faBell } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { useNotifications } from "@/hooks/useNotifications/useNotifications";
import { NotificationDrawer } from "./NotificationDrawer";

export function NotificationBell() {
  const [drawerOpen, setDrawerOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  const { unreadCount, notifications, isLoadingNotifications, markAsRead, markAllRead } =
    useNotifications(drawerOpen);

  // Close the drawer when clicking outside
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (drawerOpen && containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setDrawerOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [drawerOpen]);

  const handleToggle = () => setDrawerOpen((prev) => !prev);

  const handleRead = (id: number) => {
    markAsRead(id);
  };

  const handleMarkAllRead = () => {
    markAllRead(undefined);
    setDrawerOpen(false);
  };

  return (
    <div ref={containerRef} className="relative flex-shrink-0">
      <button
        onClick={handleToggle}
        aria-label={`Notifications${unreadCount > 0 ? `, ${unreadCount} unread` : ""}`}
        aria-haspopup="dialog"
        aria-expanded={drawerOpen}
        className="relative flex items-center justify-center w-9 h-9 rounded-lg transition-all duration-200 text-light/80 dark:text-dark/80 hover:bg-light-muted/80 dark:hover:bg-dark-surface/60 focus:outline-none focus-visible:ring-2 focus-visible:ring-light-primary dark:focus-visible:ring-dark-primary"
      >
        <FontAwesomeIcon icon={faBell} className="w-4 h-4" />

        {unreadCount > 0 && (
          <span
            aria-hidden="true"
            className="absolute -top-0.5 -right-0.5 flex items-center justify-center min-w-[16px] h-4 px-1 text-[10px] font-bold leading-none rounded-full bg-red-500 text-white"
          >
            {unreadCount > 99 ? "99+" : unreadCount}
          </span>
        )}
      </button>

      {drawerOpen && (
        <NotificationDrawer
          notifications={notifications}
          isLoading={isLoadingNotifications}
          unreadCount={unreadCount}
          onRead={handleRead}
          onMarkAllRead={handleMarkAllRead}
          onClose={() => setDrawerOpen(false)}
        />
      )}
    </div>
  );
}
